/**
 * User Settings Utility Functions
 * Handles user preferences and settings storage
 */

/**
 * Get the user's default landing page from localStorage or user data
 * @returns {string} The default landing page path
 */
/**
 * Primary app route for a subscribed product module (matches subscription plan module_id from API).
 * @param {number|string|null|undefined} moduleId
 * @returns {string|null} Path or null if unknown
 */
export const getLandingPathForSubscriptionModuleId = (moduleId) => {
  const id = moduleId != null ? Number(moduleId) : NaN;
  if (id === 1) return '/task/tasklist';
  if (id === 2) return '/documents/mydocuments';
  return null;
};

export const getUserDefaultLandingPage = () => {
  return '/dashboard';
};

/**
 * Get the user's default landing page from API (async)
 * @param {string} corporateUserId - The corporate user ID
 * @returns {Promise<string>} The default landing page path
 */
export const getUserDefaultLandingPageFromAPI = async (corporateUserId) => {
  try {
    const response = await fetch(`${process.env.REACT_APP_API_URL}/settings/corporate-user-settings/${corporateUserId}`);
    if (response.ok) {
      const data = await response.json();
      return data.defaultLandingPage || '/task/tasklist';
    }
  } catch (error) {
    console.error('Error fetching user settings from API:', error);
  }
  return '/task/tasklist';
};

/**
 * Set the user's default landing page to localStorage
 * @param {string} landingPage - The landing page path to set
 */
export const setUserDefaultLandingPage = (landingPage) => {
  localStorage.setItem('userDefaultLandingPage', landingPage);
};

/**
 * Set the user's default landing page via API
 * @param {string} corporateUserId - The corporate user ID
 * @param {string} landingPage - The landing page path to set
 * @returns {Promise<boolean>} Success status
 */
export const setUserDefaultLandingPageToAPI = async (corporateUserId, landingPage) => {
  try {
    const response = await fetch(`${process.env.REACT_APP_API_URL}/corporate-user-settings`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken')}`
      },
      body: JSON.stringify({
        corporateUserId: corporateUserId,
        defaultLandingPage: landingPage
      })
    });
    return response.ok;
  } catch (error) {
    console.error('Error saving user settings to API:', error);
    return false;
  }
};

/**
 * Get all available landing page options
 * @returns {Array} Array of landing page options
 */
export const getLandingPageOptions = () => {
  return [
    { value: '/task/tasklist', label: 'Dashboard' },
    { value: '/work-register/list', label: 'Work Register' },
    { value: '/task/projectlist', label: 'Project' },
    { value: '/task/worksheet', label: 'Worksheet' },
    { value: '/forum/dashboard', label: 'Discussion Forum' },
    { value: '/invoice-scanner/launch', label: 'Snap Bill' },
    // { value: '/documents/dashboard', label: 'Documents Dashboard' },
    // { value: '/documents/mydocuments', label: 'My Documents' },
    // { value: '/documents/shared', label: 'Shared Documents' },
    // { value: '/documents/favorites', label: 'Favorite Documents' }
  ];
};

/**
 * Normalize path for comparison (ensure leading slash)
 */
const normalizePathForOptions = (path) => {
  if (!path || typeof path !== 'string') return '';
  const trimmed = path.trim();
  return trimmed.startsWith('/') ? trimmed : '/' + trimmed;
};

/**
 * Get landing page options filtered by Access Control (only pages user can access)
 * @param {string[]} routePaths - Array of route paths from my-access API
 * @returns {Array} Filtered array of landing page options
 */
export const getAccessibleLandingPageOptions = (routePaths) => {
  const allOptions = getLandingPageOptions();
  if (!Array.isArray(routePaths) || routePaths.length === 0) {
    return allOptions;
  }
  const normalizedPaths = new Set(routePaths.map((p) => normalizePathForOptions(p)));
  return allOptions.filter((option) => {
    const path = normalizePathForOptions(option.value);
    if (normalizedPaths.has(path)) return true;
    // Forum dashboard shares access with forum list (Access Control may only grant list).
    if (path === '/forum/dashboard' && normalizedPaths.has('/forum/list')) return true;
    return false;
  });
};

/**
 * Get filtered landing page options based on user access
 * @param {boolean} hasProjectAccess - Whether user has project access
 * @param {boolean} hasWorksheetAccess - Whether user has worksheet access
 * @param {string} userRole - User's role (corporate_admin, branch_admin, department_admin, sub_department_admin, admin, user, etc.)
 * @returns {Array} Filtered array of landing page options
 */
export const getFilteredLandingPageOptions = (hasProjectAccess, hasWorksheetAccess, userRole) => {
  const allOptions = getLandingPageOptions();
  
  // Check if user can access worksheet (corporate, branch, department admin can access, but not sub department admin)
  // Helper to normalize role (handles spaces vs underscores)
  const normalizeRole = (role) => {
    if (!role) return '';
    return role.toLowerCase().replace(/\s+/g, '_').trim();
  };

  let canAccessWorksheet = false;
  if (userRole) {
    const role = normalizeRole(userRole);
    // Sub department admin cannot access worksheet
    canAccessWorksheet = (
      role === 'corporate_admin' ||
      role === 'branch_admin' ||
      role === 'department_admin' ||
      role === 'admin' // Legacy support
    );
  }
  
  return allOptions.filter(option => {
    if (option.value === '/task/projectlist') {
      return hasProjectAccess; // Show project only if user has project access
    }
    if (option.value === '/task/worksheet') {
      return hasWorksheetAccess && canAccessWorksheet; // Show worksheet only if user has worksheet access AND can access worksheet
    }
    return true; // Always show task option
  });
};

/**
 * Check if a landing page option is valid
 * @param {string} landingPage - The landing page path to validate
 * @returns {boolean} True if valid, false otherwise
 */
export const isValidLandingPage = (landingPage) => {
  const options = getLandingPageOptions();
  return options.some(option => option.value === landingPage);
};

/**
 * Normalize path for comparison (ensure leading slash)
 * @param {string} path - Route path
 * @returns {string} Normalized path
 */
const normalizePath = (path) => {
  if (!path || typeof path !== 'string') return '';
  const trimmed = path.trim();
  return trimmed.startsWith('/') ? trimmed : '/' + trimmed;
};

/**
 * Preferred order when choosing a landing page from access-control routePaths
 * (used only when the user's saved default is not among allowed paths).
 * First entry in this list that also exists in the user's allowed paths wins.
 * Aligns with getLandingPageOptions (task → project → worksheet), then other common homes.
 */
export const LANDING_PAGE_PRIORITY = [
  '/task/tasklist',
  '/work-register/list',
  '/task/projectlist',
  '/task/worksheet',
  '/forum/dashboard',
  '/forum/list',
  '/invoice-scanner/launch',
  '/documents/dashboard',
  '/documents/mydocuments',
  '/home',
  '/dashboard',
];

/**
 * Pick one path from allowed paths using LANDING_PAGE_PRIORITY; if none match, use API order fallback.
 * @param {string[]} normalizedPaths - non-empty, already normalized with leading slashes
 * @returns {string}
 */
export const pickLandingPathByPriority = (normalizedPaths) => {
  if (!Array.isArray(normalizedPaths) || normalizedPaths.length === 0) {
    return '/task/tasklist';
  }
  const allowed = new Set(normalizedPaths);
  for (const preferred of LANDING_PAGE_PRIORITY) {
    if (allowed.has(preferred)) {
      return preferred;
    }
  }
  return normalizedPaths[0];
};

/**
 * Post-login / index landing. All users go to the dashboard.
 * @returns {Promise<string>}
 */
export const getResolvedLandingPage = async () => {
  return '/dashboard';
};
